<script lang="ts">
	/**
	 * ExhibitEhrAssistant — natural-language commands → EHR actions.
	 *
	 * Beat 01 (foundation): An expert in your EHR.
	 * Pain anchor: 36 minutes in EHR per 30-minute visit (AMA 2024);
	 *              the EHR is the bottleneck under every other workflow.
	 *
	 * Visual: a small chat-of-commands transcript on the left; each
	 * command's resulting EHR actions on the right with status —
	 * done · asked-for-consent · awaiting-approval. Multi-actor
	 * (practice manager and physician) demonstrates that Ekavi answers
	 * natural language from anyone on the team.
	 *
	 * Brand discipline: the consent gate is visible. Clinical decisions
	 * never auto-execute. Ekavi asks before it touches anything that
	 * crosses the administrative line.
	 */

	import EkaviMark from '../EkaviMark.svelte';

	interface Action {
		who: 'jamie' | 'rivera' | 'lisa';
		say: string;
		result: string;
		state: 'done' | 'consent' | 'awaiting';
	}

	const ACTIONS: ReadonlyArray<Action> = [
		{
			who: 'rivera',
			say: 'Refill all routine meds for Hernandez. Hold the controlled.',
			result: '4 refills routed to MA · 1 held for review',
			state: 'consent'
		},
		{
			who: 'jamie',
			say: "Post yesterday's eligibility 271s to the charts.",
			result: '18 charts updated · 2 flagged for review',
			state: 'done'
		},
		{
			who: 'lisa',
			say: "Pull this week's denials and queue the appeals.",
			result: '3 patterns · appeals drafted',
			state: 'done'
		}
	];

	const WHO_LABELS: Record<Action['who'], string> = {
		rivera: 'Dr. Rivera (Clinician)',
		jamie: 'Jamie (Operations Manager)',
		lisa: 'Lisa (Billing Specialist)'
	};

	function whoLabel(w: Action['who']): string {
		return WHO_LABELS[w];
	}
</script>

<figure
	class="exhibit"
	aria-label="Three natural-language commands into your EHR resolving into EHR actions"
>
	<div class="head">
		<span class="eyebrow">Natural language · into your EHR</span>
		<span class="count">3 actions</span>
	</div>

	<ul class="actions">
		{#each ACTIONS as a, i (i)}
			<li class="action" data-state={a.state}>
				<div class="say-row">
					<span class="who">{whoLabel(a.who)}</span>
				</div>
				<p class="say">"{a.say}"</p>
				<div class="result-row">
					<span class="rail" aria-hidden="true">
						<EkaviMark size={12} decorative maskIdSuffix={`agent-${i}`} />
					</span>
					<span class="result">{a.result}</span>
					<span class="state state-{a.state}">
						{#if a.state === 'done'}done{:else if a.state === 'consent'}consent gate{:else}needs you{/if}
					</span>
				</div>
			</li>
		{/each}
	</ul>

	<p class="foot">Anything clinical pauses for sign-off.</p>
</figure>

<style>
	.exhibit {
		background: var(--ekavi-paper);
		border: var(--ekavi-stroke-hairline);
		padding: var(--ekavi-space-6);
		margin: 0;
	}
	.head {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: var(--ekavi-space-3);
		padding-bottom: var(--ekavi-space-3);
		border-bottom: var(--ekavi-stroke-hairline);
		margin-bottom: var(--ekavi-space-4);
	}
	.eyebrow {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
	}
	.count {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		font-variant-numeric: tabular-nums;
	}

	.actions {
		list-style: none;
		padding: 0;
		margin: 0 0 var(--ekavi-space-4);
	}
	.action {
		padding-block: var(--ekavi-space-3);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.action:last-child {
		border-bottom: none;
	}

	.say-row {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: var(--ekavi-space-3);
		margin-bottom: var(--ekavi-space-1);
	}
	.who {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
		font-weight: var(--ekavi-weight-halbfett);
	}
	.ehr {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
	}
	.say {
		margin: 0 0 var(--ekavi-space-2);
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
		line-height: 1.5;
	}

	.result-row {
		display: grid;
		grid-template-columns: 16px minmax(0, 1fr) auto;
		align-items: center;
		gap: var(--ekavi-space-2);
	}
	.rail {
		display: inline-flex;
		justify-content: center;
		color: var(--ekavi-charcoal);
	}
	.result {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-2);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.state {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		text-transform: lowercase;
	}
	.state-done {
		color: var(--ekavi-sage);
	}
	.state-consent {
		color: var(--ekavi-ochre);
		font-weight: var(--ekavi-weight-halbfett);
	}
	.state-awaiting {
		color: var(--ekavi-ochre);
		font-weight: var(--ekavi-weight-halbfett);
	}

	.foot {
		margin: var(--ekavi-space-3) 0 0;
		padding-top: var(--ekavi-space-3);
		border-top: var(--ekavi-stroke-hairline);
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
	}

	@media (max-width: 480px) {
		.result-row {
			grid-template-columns: 16px 1fr;
		}
		.state {
			grid-column: 2 / -1;
			grid-row: 2;
			margin-top: 2px;
		}
	}
</style>
